import { Flex, Text } from "flicket-ui";
import { useAtom } from "jotai";
import flatten from "lodash/flatten";
import startCase from "lodash/startCase";
import { useRouter } from "next/router";
import { ReactNode, useContext, useEffect, useState } from "react";
import styled from "styled-components";
import useSWR from "swr";
import { BaseContext } from "~context";
import { isBNPLIntegrationType } from "~features/checkout/checkout.types";
import {
  INormalisedData,
  IReleaseTicket,
  IReleaseZone,
} from "~features/nonseated-reservation/nonseated-reservation.types";
import { useNonSeatedReservationStore } from "~features/nonseated-reservation/store/non-seated-reservation-store";
import useActiveReferralCampaign from "~features/referrals/useActiveReferralCampaign";
import {
  IntegrationType,
  LandingPage,
  ReleaseType,
  TaxCalculationMethod,
  TicketTypeKind,
} from "~graphql/sdk";
import { MembershipsSoldForUserDocument } from "~graphql/typed-document-nodes";
import {
  CreateOrderFn,
  useAccount,
  useIsMobile,
  useNonSeatedMultibuy,
  useOrganization,
  useReferral,
} from "~hooks";
import { googleAnalyticsReleaseAtom } from "~hooks/useGoogleAnalyticsPageView";
import { useQuery } from "~hooks/useQuery";
import {
  OrganizationFeatures,
  calculateMultibuyDiscount,
  calculateTotal,
  sdk,
  showToast,
  trackViewItemList,
} from "~lib";
import {
  AttributeNames,
  InteractionNames,
} from "~telemetry/enums/AttributeNames";
import useCaptureClick from "~telemetry/traces/useCaptureClick";
import * as actions from "~features/nonseated-reservation/store/actions";
export interface INonSeatedProps {
  data: INormalisedData;
  zones: IReleaseZone[];
  addons: IReleaseTicket[];

  createOrder: CreateOrderFn;
  isMembership: boolean;
  releaseType: ReleaseType;
  releaseNotes: string;
  isDigital: boolean;
  isResaleRelease?: boolean;
  allowPublicComp?: boolean;
  requiresAuth: boolean;
}

export type TSoldOutWaitlistMessage = ({
  zoneName,
}: {
  zoneName: string;
}) => JSX.Element;

const StyledLink = styled.a`
  text-decoration: underline;
`;

export const useViewModel = (props: INonSeatedProps) => {
  const {
    data,
    addons: availableAddons = [],
    zones = [],
    createOrder,
    isMembership,
    releaseNotes,
    isDigital,
    isResaleRelease,
    releaseType,
    allowPublicComp,
    requiresAuth,
  } = props;

  const { activeReferralCampaign } = useActiveReferralCampaign(data.id);
  const router = useRouter();
  const { isAdmin, isPOS, isLoggedIn, user } = useAccount();

  const selectedTicketTypes = useNonSeatedReservationStore(
    (state) => state.selectedTicketTypes[data.id] ?? []
  );

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [delayedIsSubmitting, setDelayedSubmitting] = useState(false);
  const { organization, isSinglePage } = useContext(BaseContext);
  const { hasFeature } = useOrganization();
  const [importantNotice, setImportantNotice] = useState(releaseNotes);
  const [alertError, setAlertError] = useState<
    { title: string; message: string | ReactNode } | undefined
  >();
  const ticketVariant = isDigital ? "pass code" : "ticket";
  const isMobile = useIsMobile();
  const { calculateReferralDiscount } = useReferral();

  const selectedTickets = selectedTicketTypes?.filter(
    (i) => i.type !== "addon"
  );
  const selectedAddons = selectedTicketTypes?.filter((i) => i.type === "addon");
  const userCredits = user?.credits ?? 0;

  const bnplGateways = data?.gateways?.filter(
    (gateway) => !!gateway && isBNPLIntegrationType(gateway.type)
  );

  const stripeAfterpayGateway = data?.gateways?.find(
    (gateway) =>
      gateway.type === IntegrationType.PaymentStripe && gateway.bnplEnabled
  );

  const showHiddenFees = !!organization.features?.includes(
    OrganizationFeatures.ShowHiddenFees
  );

  useEffect(() => {
    let timer: NodeJS.Timeout = undefined;
    if (isSubmitting) {
      timer = setTimeout(() => {
        setDelayedSubmitting(true);
      }, 2000);
    } else {
      setDelayedSubmitting(false);
    }
    return () => timer && clearTimeout(timer);
  }, [isSubmitting]);

  useEffect(() => {
    releaseNotes
      ? setImportantNotice(releaseNotes)
      : setImportantNotice(data?.importantNotice);
  }, [releaseNotes]);

  const [release] = useAtom(googleAnalyticsReleaseAtom);

  const { data: ticketsSoldForUserData } = useSWR(
    isLoggedIn && data?.id && !isMembership
      ? ["tickets-sold-for-user", organization.id, data?.id]
      : null,
    async (_: string, orgId: string, eventId: string) =>
      sdk({ orgId })
        .ticketsSoldForUser({ eventId })
        .then(({ ticketsSoldForUser }) => ticketsSoldForUser)
  );

  const { data: membershipsSoldForUserData } = useQuery(
    isLoggedIn && data?.id && isMembership && MembershipsSoldForUserDocument,
    {
      membershipId: data?.id,
    }
  );

  const captureClick = useCaptureClick({
    eventId: !isMembership && data?.id,
    forwardToNest: true,
    pageName: "ticket-reservation-page",
    attributes: {
      [AttributeNames.IS_MEMBERSHIP]: isMembership,
    },
  });

  const membershipsSoldForUser =
    membershipsSoldForUserData?.membershipsSoldForUser;

  const ticketsSoldForUser =
    ticketsSoldForUserData ?? membershipsSoldForUser ?? [];

  /** @note because GA multibuy and non GA multibuy differ too much we have a new hook */
  const { activePromotions } = useNonSeatedMultibuy(
    data?.multiBuyPromotions,
    selectedTickets.map((s, key) => ({
      ...s,
      id: `${s.id}-${key}`,
      [isMembership ? "membershipType" : "ticketType"]: { id: s.id },
    }))
  );

  const allTicketTypes = flatten(zones.map((zone) => zone.ticketTypes)).filter(
    (ticketType) => {
      if (
        ticketType.kind !== TicketTypeKind.Standard &&
        !(isAdmin || allowPublicComp)
      ) {
        return false;
      }

      return ticketType.isActive;
    }
  );

  const addonTypes = availableAddons?.filter(
    (addon) =>
      isAdmin ||
      addon.isActive ||
      (release && release.type !== ReleaseType.Listed)
  );

  const allTicketTypesAndAddons = [...addonTypes, ...allTicketTypes];
  const selectedTicketTypesAndAddons = [...selectedTickets, ...selectedAddons];

  const multibuyDiscount = selectedTickets?.every(
    (seat) => seat.zoneId === selectedTickets?.[0]?.zoneId
  )
    ? calculateMultibuyDiscount(
        activePromotions[0],
        allTicketTypesAndAddons,
        zones?.find((zone) => zone.name === selectedTickets?.[0]?.zoneId)
          ?.ticketTypes
      )
    : 0;

  const onChangeQuantity = (id: string, value: number) => {
    const ticketType = allTicketTypesAndAddons.find((tt) => tt.id === id);
    console.log({ id, value, ticketType });

    actions.updateSelectedTicketTypes({
      id: data.id,
      ticketType,
      quantity: value,
    });
  };

  useEffect(() => {
    if (router.query.ticket && allTicketTypes?.length > 0) {
      // If we have a ticket id in the query this will scroll the page to
      // the ticket listing
      const element = document.getElementById(router.query.ticket as string);
      element?.scrollIntoView({ behavior: "smooth" });
    }
  }, [router.query.ticket, allTicketTypes?.length]);

  const handleOrderCreation = async () => {
    setIsSubmitting(true);
    if (selectedTickets.length === 0 && selectedAddons.length === 0) {
      showToast(
        `No ${isMembership ? "memberships" : ticketVariant} selected!`,
        "error"
      );
      return setIsSubmitting(false);
    }

    try {
      await createOrder(
        selectedTickets?.filter((seat) => {
          return seat.quantity > 0;
        }),
        selectedAddons,
        activePromotions?.[0]?.promotion?.id,
        (errorMessage) => {
          setAlertError({
            title: "There was a problem...",
            message: errorMessage,
          });

          return setIsSubmitting(false);
        }
      );

      // Order is succesfully created so we can reset the reservation store state
      actions.resetInitialState();
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (zones?.length > 0) {
      trackViewItemList({
        id: data.id,
        name: data.name,
        zones,
        allTicketTypes: allTicketTypes,
        selectedTicketTypes: selectedTickets,
        ticketsSoldForUser,
        isAdmin,
        viewableAddons: addonTypes,
        release,
      });
    }
  }, [zones.length]);

  const hasTicketSelected =
    selectedTickets?.length > 0 || selectedAddons?.length > 0;

  const totalOptions = {
    showHiddenFees,
    fee: data?.bookingFee,
    type: data?.bookingFeeType,
  };

  const subTotal = calculateTotal(selectedTicketTypesAndAddons, totalOptions);
  const lineItemsTotal = calculateTotal(selectedTicketTypesAndAddons);

  const referralDiscount = calculateReferralDiscount(
    activeReferralCampaign,
    lineItemsTotal - multibuyDiscount
  );

  let totalPrice = subTotal - multibuyDiscount - referralDiscount;

  const taxRate = data.taxRate;
  const customTaxRate = data.customTaxRate;

  // note if taxes are inclusive then the total does not need taxes to be applied.
  const calculatedTaxes =
    data.taxCalculationMethod === TaxCalculationMethod.Exclusive && taxRate > 0
      ? (lineItemsTotal - multibuyDiscount - referralDiscount) * taxRate
      : 0;
  // note custom taxes are applied when the value is set at the org level on line items.
  const calculatedCustomTaxes =
    customTaxRate > 0 ? lineItemsTotal * customTaxRate : 0;

  totalPrice = totalPrice + calculatedTaxes + calculatedCustomTaxes;

  const showIsR18 = data.isR18;

  const SoldOutWaitlistMessage: TSoldOutWaitlistMessage = ({ zoneName }) => {
    const ticketWaitlistLink = `/events/${data.id}/ticket-waitlist${
      release ? `?release=${release.id}` : ""
    }`;

    return (
      <Flex justifyContent={"center"}>
        <Text variant="extraBold.M">
          {zoneName} sold out –{" "}
          <StyledLink
            onClick={captureClick(
              (e) => {
                e.preventDefault();
                void router.push(ticketWaitlistLink);
              },
              "join-waitlist",
              {
                [AttributeNames.FLICKET_ZONE_NAME]: zoneName,
              }
            )}
            href={ticketWaitlistLink}
          >
            join the waitlist
          </StyledLink>{" "}
          for ticket updates
        </Text>
      </Flex>
    );
  };

  const hasResaleWaitlist = data.ticketResaleWaitlistEnabled ?? false;

  const primaryButtonLabel = (() => {
    if (organization.id === "2648ddb4-b50c-489f-a3eb-ae0249e3db3c") {
      return "Join now";
    }

    return `Buy ${
      organization.landingPage === LandingPage.SingleEvent
        ? "ticket"
        : isMembership
        ? "membership"
        : ticketVariant
    }s`;
  })();

  const onOrderCreationClick = captureClick(
    () => {
      void handleOrderCreation();
    },
    InteractionNames.BUTTON_PRIMARY,
    {
      [AttributeNames.BUTTON_LABEL]: primaryButtonLabel,
    }
  );

  const isExperienceSales = hasFeature("showExperienceSales");

  const ticketTypeVariantTitle = isSinglePage
    ? `${startCase(ticketVariant)}s`
    : isMembership
    ? "Memberships"
    : `${startCase(ticketVariant)}s`;

  function onClickZone(releaseZoneId) {
    void router.push(`/events/${data.id}/listing/${releaseZoneId}`);
  }

  return {
    ...props,
    zones,
    isExperienceSales,
    delayedIsSubmitting,
    importantNotice,
    showIsR18,
    ticketTypeVariantTitle,
    hasResaleWaitlist,
    showHiddenFees,
    isAdmin,
    selectedTickets,
    selectedAddons,
    releaseType,
    requiresAuth,
    ticketsSoldForUser,
    SoldOutWaitlistMessage,
    showWaitlistMessage:
      hasFeature("ticketResale") && hasResaleWaitlist && !isResaleRelease,
    addonTypes,
    multibuyDiscount,
    lineItemsTotal,
    activeReferralCampaign,
    hasTicketSelected,
    userCredits,
    user,
    totalPrice,
    isPOS,
    bnplGateways,
    stripeAfterpayGateway,
    alertError,
    setAlertError,
    isSubmitting,
    primaryButtonLabel,
    isMobile,
    orgCurrency: organization.currency,
    referralDiscount,
    events: {
      onChangeQuantity,
      onOrderCreationClick,
      onClickZone,
    },
  };
};
